/**
 * The website, ad accounts and campaigns a rule can point at, read live off the asset
 * store — no API call, since `setAssetForId` already fans out across every ad account
 * and flattens their campaigns. The shapes are the prototype's, deliberately, so the
 * ported consumers did not have to change.
 */
import { AdAccountDTO } from 'api-models'
import { useMemo } from 'react'
import { useAssetStore } from 'store'
import { RulePlatform } from './types'

export interface AdAccountOption {
	id: string
	name: string
	platform: RulePlatform
}

export interface CampaignOption {
	id: string
	name: string
	accountId: string
}

export interface WebsiteEntity {
	name: string
}

export interface RuleEntities {
	website: WebsiteEntity
	adAccounts: AdAccountOption[]
	campaigns: CampaignOption[]
}

/** `AdAccountDTO.platform` is the storage code; the ported components speak the
 *  prototype's slugs. BADS is Microsoft Ads — the UI name is "Microsoft". */
const PLATFORM_BY_CODE: Record<AdAccountDTO['platform'], RulePlatform> = {
	GADS: 'google-ads',
	FADS: 'meta',
	BADS: 'microsoft'
}

export const toAdAccountOption = (
	adAccount: AdAccountDTO
): AdAccountOption => ({
	id: adAccount.id,
	name: adAccount.name,
	platform: PLATFORM_BY_CODE[adAccount.platform] ?? 'google-ads'
})

/** Live entity lists for the current asset. Campaigns come back typed as a
 *  Google/Facebook union and Bing ones flow through mistyped (a known wrinkle, not
 *  introduced here); only `id`, `name` and `ad_account_id` are read, and all three
 *  exist on every variant. */
export const useRuleEntities = (): RuleEntities => {
	const asset = useAssetStore((s) => s.asset)
	const adAccounts = useAssetStore((s) => s.adAccounts)
	const campaigns = useAssetStore((s) => s.campaigns)

	return useMemo(
		() => ({
			website: { name: asset?.name || asset?.domain || '' },
			adAccounts: adAccounts.map(toAdAccountOption),
			campaigns: campaigns.map((campaign) => ({
				id: campaign.id,
				name: campaign.name,
				accountId: campaign.ad_account_id
			}))
		}),
		[asset?.name, asset?.domain, adAccounts, campaigns]
	)
}
